/*********************************************************
 * Third Components
*********************************************************/
import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Icon } from '@mui/material';
/*********************************************************
 * Components
*********************************************************/
import { Select, Button, Popover } from 'Components';
import Table, { Column, Toolbar, DeleteButton } from 'Components/Table';
import { ExportButton } from 'Components/TableView/Toolbar';
import { ExceptionManager } from 'Core/logManager';
import { cutNotes } from 'Core/data/Helpers'

import { LandmarkCircleRadiusValues } from 'Components/Map/providers/enums'

/*********************************************************
 * Redux
*********************************************************/
import {
  searchLandmarks,
  showModalBulkDeleteLandmark,
  showModalBulkEditLandmark,
  showModalEditLandmark,
  showModalDeleteLandmark
} from 'Redux/actions';

/*********************************************************
 * Css
*********************************************************/
import './style.scss'

/*********************************************************
 * Class to manage landmarks
*********************************************************/
const LandmarkListEdit = (props) => {
  const tableRef = useRef();
  const dispatch = useDispatch();

  const { messages } = props

  const shouldClearLandmarks = false
  const landmarksPerPage = 10
  const [groupSelect, setGroupSelect] = useState();
  const [landmarkCheckedList, setlandmarkCheckedList] = useState([]);
  const [landmarksItems, setLandmarkItems] = useState([])
  const [editBtn, setEditBtn] = useState(0)

  const getHeaderToExport = useMemo(() => {
    return [
      messages["realtimemaps_landmarks_grid_title"],
      messages["realtimemaps_landmarks_grid_address"],
      messages["realtimemaps_landmarks_grid_latlng"],
      messages["realtimemaps_landmarks_grid_radius"],
      messages["realtimemaps_landmarks_grid_groups"],
      messages["realtimemaps_landmarks_grid_notes"]
    ];
  });

  /*********************************************************
   * Get the redux properties
  *********************************************************/
  const {
    landmarks,
    loading
  } = useSelector((state) => state.entitySelectorRedux);

  const {
    process,
    loadingLandmark,
    landmarkGroups,
    loadingLandmarkGroups
  } = useSelector((state) => state.realtimeMapLandmarkModuleRedux);

  useEffect(() => {
    try {
      if (!loadingLandmark && process) {
        setGroupSelect(landmarkGroups[0]);
        dispatch(searchLandmarks(0, 0, '', shouldClearLandmarks, landmarkGroups[0]?.id, true));
        setlandmarkCheckedList([]);
      }
    } catch (exc) {
      ExceptionManager(exc, 'modules/realtimeMaps/main/components/LandmarksListMain/components/LandmarkListEdit/index.js', 'LandmarkListEdit');
    }
  }, [loadingLandmark, process]);

  /*********************************************************
   * set the landmarks elements into the state
  *********************************************************/
  useEffect(() => {
    if (landmarks) {
      setLandmarkItems([...landmarks])
    }
  }, [landmarks]);

  /*********************************************************
   * Get the first landmarks data
  *********************************************************/
  useEffect(() => {
    //if not selected item and have landmarks groups elements
    if (!groupSelect && landmarkGroups && landmarkGroups.length > 0) {
      setGroupSelect(landmarkGroups[0])
      dispatch(searchLandmarks(0, 0, '', shouldClearLandmarks, landmarkGroups[0].id, true));
    }
  }, [landmarkGroups]);

  //** pdf csv functions */
  const getDataToExport = (rows) => {
    let items = rows ? rows : landmarksItems;
    let data = [];
    items.forEach((item) => {
      const title = item.name
      const address = item.address ? item.address : "";
      const latlng = `${item?.latitude.toFixed(6)} / ${item?.longitude.toFixed(6)}`
      const radius = item.radius ? item.radius : "";
      const groups = item.groupName
      const notes = cutNotes(item.note, 200)

      data.push([title, address, latlng, radius, groups, notes]);
    });

    return data;
  }

  /*********************************************************
   * Event when the user select another landmark group
  *********************************************************/
  const onChangeLandmarkGroup = (event) => {
    setGroupSelect(event)
    //dispatch(searchLandmarks(landmarksPerPage, 0, '', shouldClearLandmarks, event.id));
    dispatch(searchLandmarks(0, 0, '', shouldClearLandmarks, event.id, true));
    tableRef?.current?.resetPagination()
  }

  /*********************************************************
   * Perform the bulk of landmarks
  *********************************************************/
  const onBulkDeleteLandmarks = () => {
    dispatch(showModalBulkDeleteLandmark(true, landmarkCheckedList))
  }

  /*********************************************************
   * Perform bulk edit landmarks
  *********************************************************/
  const onBulkEditLandmarks = () => {
    dispatch(showModalBulkEditLandmark(true, landmarkCheckedList))
  }

  const getTableHeight = () => {
    let screenWidth = window.innerWidth;

    if (screenWidth > 1280 && screenWidth <= 1366) {
      return 200;
    } else if (screenWidth <= 1280) {
      return 100;
    } else {
      return 350;
    }
  }

  return (
    <div className='paddingLandmarkEdit'>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
        <Grid item xs={12}>
          <Table
            viewStyle={'small'}
            data={landmarksItems}
            rowsPerPage={landmarksPerPage}
            loading={loading || loadingLandmark || loadingLandmarkGroups}
            hideSelectableCheckbox={false}
            //stickyHeader
            name="landmarkgrid"
            exportOrientation="landscape"
            getDataToExport={getDataToExport}
            setSelectedItems={(selectedLandmark) => {
              setlandmarkCheckedList(selectedLandmark);
            }}
            height={getTableHeight()}
            heightAuto

            /** enable reorder column */
            orderColumn={false}
            /** enable column visibility */
            setColumnVisibility={false}
            /** enable column resizing */
            setColumnResizing={false}
            /** setting preferences */

            //visibleColumns={visibleColumns}
            //columnsOrder={columnOrder}
            //columnResize={columnResize}

            globalHeadersCellStyles={{ background: "white", borderTop: "1px solid #e0e0e0", height: "50px", paddingLeft: "2px" }}
            //watchSearchDataChange={watchSearchDataChange}
            /** hide options */
            hideDownloadButton={false}
            hidePdfOption={false}
            getHeaderToExport={getHeaderToExport}
            paddingRight={1}
            ref={tableRef}
          >
            <Toolbar className='paddingLandmarkToolbar'>
              <div className='landmarkGroup landmarkGroupList'>
                <Select
                  id="landmarkGroup"
                  //label={messages['realtimemaps_landmarks_groups_select_name']}
                  value={groupSelect}
                  onChange={(event) => onChangeLandmarkGroup(event)}
                  options={landmarkGroups || []}
                  textField="name"
                  valueField="id"
                  placeholder={messages['realtimemaps_landmarks_groups_select_placeholder']}
                />
              </div>
              <DeleteButton
                tooltip={messages["realtimemaps_landmarks_bulk_delete"]}
                onClick={(event) => {
                  event.preventDefault();
                  onBulkDeleteLandmarks();
                }}
                disabled={landmarkCheckedList.length === 0}
              />
              <Button
                type="icon"
                tooltip={messages["realtimemaps_landmarks_groups_edit_modal"]}
                placement="bottom"
                icon="edit" {...props}
                disabled={landmarkCheckedList.length === 0}
                onClick={(event) => {
                  event.preventDefault()
                  onBulkEditLandmarks()
                }}
              />
            </Toolbar>
            <Column
              label={' ' + messages['realtimemaps_landmarks_grid_title']}
              field="name"
              headerClassName="centerContent"
              customValue={(item) => {
                return item?.name || 0;
              }}
            >
              {(item) => {
                return (
                  <div
                    onMouseEnter={() => setEditBtn(item.id)}
                    onMouseLeave={() => setEditBtn(0)}
                  >
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item xs>
                        <span>{item?.name}</span>
                      </Grid>

                      {
                        editBtn == item?.id
                        &&
                        <Grid item xs className="text-right">
                          <ExportButton
                            className='noClass'
                            firstOption={messages["edit"]}
                            secondOption={messages["delete"]}
                            icon={'dehaze'}
                            onExport={(event) => {
                              if (event == messages["edit"]) {
                                dispatch(showModalEditLandmark(true, [item]))
                              }
                              if (event == messages["delete"]) {
                                dispatch(showModalDeleteLandmark(true, [item]))
                              }

                            }}
                          />
                        </Grid>
                      }
                    </Grid>
                  </div>

                )
              }}
            </Column>
            <Column
              label={messages['realtimemaps_landmarks_grid_address']}
              field="address"
              headerClassName="centerContent"
            >
              {(item) => {
                return (
                  <span>{item?.address}</span>
                )
              }}
            </Column>
            <Column label={messages['realtimemaps_landmarks_grid_latlng']} field="latlng">
              {(item) => {
                return <span className="default_padding">{`${item?.latitude.toFixed(6)} / ${item?.longitude.toFixed(6)}`}</span>;
              }}
            </Column>
            <Column
              label={messages['realtimemaps_landmarks_grid_radius']}
              field="radius"
              customValue={(item) => {
                return parseInt(item?.radius);
              }}
            >
              {(item) => {
                const radius = LandmarkCircleRadiusValues.find(r => r?.value && item?.radius && `${r?.value}` == `${item?.radius}`);
                if (radius) {
                  return <span>{radius?.text}</span>;
                }

                return <span>{item?.radius}</span>;
              }}
            </Column>
            <Column label={messages['realtimemaps_landmarks_grid_groups']} field="groupName">
              {(item) => {
                return <span>{item?.groupName}</span>;
              }}
            </Column>
            <Column
              label={messages['realtimemaps_landmarks_grid_notes']}
              field="notes"
              customValue={(item) => {
                return item?.note || 0;
              }}
            >
              {(item) => {
                const notes = cutNotes(item.note, 10)
                return (
                  <div>
                    {notes && (
                      <Popover
                        component={
                          <Button
                            type="icon"
                            onClick={() => {

                            }}
                            icon="comment"
                          />
                        }
                      >
                        <div style={{ padding: 12 }}>{notes}</div>
                      </Popover>
                    )}
                  </div>
                );
              }}
            </Column>
          </Table>
        </Grid>
      </Grid>
    </div>
  )

}

export default LandmarkListEdit




